// ---- Humanoid archetype (ROUNDED — plan 27, folded in) ----
// THE shared biped body: a parameterised, FULLY-JOINTED humanoid whose limbs
// are tapered capsules (`bone`), whose torso/head/hair are custom lathe
// silhouettes, and whose face is smooth balls + one arc smile — the rounded
// art style locked in the plan-27 studio iteration (this file absorbed
// humanoid-round.ts; the old boxy geometry is gone). Every humanoid model doc,
// the player, remotes, cinematic puppets and the studio all build through
// here, so a look change lands everywhere at once.
//
// It publishes the exact humanoid anchor set and rigs the shared RIG-CONTRACT
// joint names (src/model/rig.ts): hips/knees, shoulders/elbows, torso, head +
// handMain/handOff sockets — the SAME names the clip system and the shared
// gait (gait.ts) drive, so clips/stances/holds need no adapter.
//
// Three axes of variation, all composable:
//   • `sex`   — the female frame (HUMANOID_TUNING.female): slimmer limbs,
//               rounder hips, waist pinch, a front-only bust bulge.
//   • `build` — a named silhouette preset (BUILD_PRESETS: average/stout/
//               slight/squat): girth, torso width/depth and leg length.
//   • the doc — palette + scale + lower/arms/head params, same as ever.
//
// Iterate the look in HUMANOID_TUNING below ↓ — every limb's end radii in one
// place (all multiplied by the body scale `s` at the use site). [0] = the
// radius at the UPPER joint (hip / shoulder / knee / elbow), [1] = at the
// LOWER end (knee / ankle / elbow / wrist). Caps sit centered ON the joints,
// so consecutive bones share a ball at the joint and stay seamless while the
// gait folds them.

import type { Prims } from "../meshkit.ts";
import { shade } from "../color.ts";
import { ARCHETYPE_ANCHORS, type Vec3 } from "./parts.ts";
import { buildHumanoidFace } from "./face.ts";
import type { BodyBuild, BeardStyle, FaceExpression, HairStyle, Sex } from "../shared.ts";

/** Named points on the shared head frame (HUMANOID_TUNING.scalp). Math only —
 *  hair + beard recipes path locks between these; nothing draws them by default.
 *  Hair points sit on the upper skull; jaw/chin/lip points dress the face. */
export type ScalpPt =
  | "hairline" | "hairlineL" | "hairlineR"
  | "templeL" | "templeR"
  | "earL" | "earR"
  | "crown" | "occipital" | "nape"
  // Beard / face map (same headY-local space as hair)
  | "cheekL" | "cheekR"
  | "jawL" | "jawR"
  | "chin" | "chinL" | "chinR"
  | "lip" | "underlip";

/** Coverage shell ids — which band of the scalp a lathe mass covers. */
export type HairShellId = "full" | "high" | "crown";

export type HumanoidOptions = {
  /** Skin colour (face, hands). */
  skin: number;
  /** Overall size multiplier. Default 1. */
  scale?: number;
  /** Body-type frame preset. Default "male". "female" = slimmer limbs, rounder
   *  hips, a tapered waist + a small bust (HUMANOID_TUNING.female). */
  sex?: Sex;
  /** Named silhouette preset (shared BODY_BUILDS): girth/width/leg-length
   *  multipliers over the shared frame. Default "average". Composes with sex. */
  build?: BodyBuild;
  /** Lower body: trousered legs or a full-length skirt/robe. `short` renders
   *  the shin in skin (shorts). */
  lower:
    | { kind: "legs"; color: number; boot?: number; short?: boolean }
    | { kind: "skirt"; color: number; hem?: number };
  /** Torso block. (width/height/depth are legacy no-ops — the silhouette is
   *  the tuning's lathe profile; use `build` presets to vary it.) */
  torso: { color: number; width?: number; height?: number; depth?: number };
  /** Hanging arms. Default sleeve = torso colour. */
  arms?: { sleeve?: number; length?: number };
  /** Head details. Styles are the shared HAIR_STYLES / BEARD_STYLES sets
   *  (shared.ts) — every entry there must build here. Set `features: false` to
   *  skip the default eyes/brows/mouth — use when a `slots.face` part (e.g.
   *  goblin-face) is the whole face. Built-in beards keep features on (they
   *  add jaw/chin mass under the smile). `beard` is an optional color override
   *  (defaults to hair color, then a skin-shade when bald). `expression` picks
   *  the face pose (default "happy"; see src/model/face.ts). */
  head?: {
    hair?: number;
    hairStyle?: HairStyle;
    beard?: number;
    beardStyle?: BeardStyle;
    eye?: number;
    features?: boolean;
    expression?: FaceExpression;
  };
};

export const HUMANOID_TUNING = {
  thigh: [0.095, 0.07] as const, // hip → knee
  shin: [0.066, 0.046] as const, // knee → ankle
  /** Upper arm = shoulder → elbow, but ALSO the deltoid: a FAT top (the
   *  shoulder muscle) tapering to a slim elbow. There is no separate deltoid
   *  ball — the arm is ONE continuous capsule whose fat top cap is the
   *  shoulder, so there's no arm-side seam by construction. The fat top is
   *  rooted UP + IN (small shoulderDrop, small armInset) so it plugs straight
   *  into the widened torso shoulder shelf — arm and shoulder read as one mass
   *  emerging from the body. [0] = deltoid radius, [1] = elbow radius. */
  upperArm: [0.092, 0.05] as const,
  /** How far below shoulderY the fat arm top sits — kept small so the deltoid
   *  rides up level with the torso's shoulder shelf. */
  shoulderDrop: 0.055,
  /** Arm distance from center beyond chestW/2 — pulled well IN so the fat
   *  deltoid top overlaps deep into the torso (the arm plugs into the body,
   *  not beside it). */
  armInset: 0.018,
  forearm: [0.056, 0.042] as const, // elbow → wrist
  /** Muscle bellies — a subtle mid-swell per limb so segments read as muscle,
   *  not straight cones. `amount` = fractional radius gain at the peak; `at` =
   *  where the peak sits (0 = the UPPER joint / r0 end, 1 = the lower). Deltoid/
   *  bicep sit high on the arm; the calf sits just below the knee. Keep small. */
  belly: {
    thigh: { amount: 0.14, at: 0.34 }, // quad, upper-third
    shin: { amount: 0.19, at: 0.24 }, // calf, just below the knee
    upperArm: { amount: 0.05, at: 0.55 }, // subtle lower-bicep fullness — the fat top cap already gives the deltoid shape
    forearm: { amount: 0.12, at: 0.3 }, // brachioradialis, near the elbow
  },
  /** Ankle height — where the shin's lower cap centers (tucks into the boot). */
  ankleY: 0.16,
  /** Rounded boot — ONE flattened heel→toe capsule + an ankle bridge. NO
   *  separate sole (a proud sole capsule/box reads as a shelf/platform); the
   *  single boot capsule is squashed vertically (`depth`) so its underside is
   *  only gently domed → flat-ish, no rim sticking out. Tapers to a rounded toe.
   *   • boot  — the whole shoe, one capsule, bottom at y≈0.
   *   • ankle — a short bone bridging the shin, ending INSIDE the boot mass. */
  boot: {
    // The leg drops at z=0, so the ankle is at z≈0. The foot extends FORWARD
    // from there with only a small rounded heel behind — heelZ sits AT the leg
    // (its cap = the little heel), toeZ reaches forward. Anatomically the ankle
    // lands at ~the back quarter of the foot.
    y: 0.0253, heelZ: 0.0, toeZ: 0.14, // boot capsule endpoints (y set so bottom ≈ 0)
    heelR: 0.055, toeR: 0.042, depth: 0.46, // taper + vertical squash → flat-ish, no shelf
    ankleTopY: 0.15, ankleBotY: 0.055, ankleZ: 0.0, // ankle bone (at the leg axis), ends inside the boot
    ankleR: 0.045, ankleBotR: 0.048,
  },
  // ---- CUSTOM SILHOUETTES (lathe profiles, [radius, y] bottom→top, ×s) ----
  // One continuous curve per body mass — hips round under, waist pinches,
  // chest bulges, shoulders round off. Edit the pairs, refresh, judge.
  // Absolute-y profiles (torso/belt/head/bust) are authored against the
  // NOMINAL 1.06 hip line — a build preset that shortens the legs slides them
  // all down together (the `dy` shift in the builder).
  /** Torso tunic: hip round-under → waist pinch → chest → shoulder round-off. */
  torsoProfile: [
    [0, 0.94], [0.14, 0.965], [0.195, 1.03], [0.205, 1.08], // hips
    [0.175, 1.22], [0.17, 1.3], // waist pinch
    [0.215, 1.42], [0.23, 1.5], // chest
    [0.238, 1.575], [0.222, 1.632], [0.14, 1.671], [0, 1.69], // BROAD shoulder shelf (meets the fat arm top) → round-off
  ] as const,
  torsoDepth: 0.58,
  /** Belt: a squat proud lens over the hip line. */
  beltProfile: [[0, 1.05], [0.215, 1.07], [0.215, 1.12], [0, 1.14]] as const,
  /** Skirt/robe lower body: ONE bell lathe from the ground to the hip line
   *  (replaces legs — no leg joints on a skirt build). y's are NOMINAL
   *  hip-space (hip = 1.06): the builder scales them by the preset's legLen so
   *  the waist tracks shortened legs; the top tucks under the torso lathe. */
  skirtProfile: [
    [0, 0.02], [0.3, 0.03], [0.355, 0.09], [0.345, 0.2],
    [0.3, 0.45], [0.255, 0.7], [0.225, 0.9],
    [0.205, 1.04], [0.19, 1.1], [0, 1.13],
  ] as const,
  /** Hem trim: a short, slightly-prouder band hugging the skirt's foot. */
  skirtHemProfile: [
    [0, 0.0], [0.32, 0.012], [0.372, 0.07], [0.34, 0.13], [0, 0.15],
  ] as const,
  skirtDepth: 0.8,
  /** Egg head (centered on headY = 1.87 nominal). */
  headProfile: [
    [0, 1.705], [0.09, 1.73], [0.14, 1.79], [0.155, 1.87],
    [0.145, 1.95], [0.1, 2.01], [0, 2.045],
  ] as const,
  headDepth: 0.92,
  /** Hair: a soft, full cap dome, ys LOCAL to headY (1.87). The brim starts
   *  LOW (a touch above the brows) and hugs the scalp, swelling gently then
   *  doming to a smooth rounded crown — a natural head of hair, not a high
   *  helmet. A mild BACK tilt (hairTilt, rad) lets the front sit as a soft
   *  fringe above the brows while the nape drops behind the ears. */
  hairProfile: [
    [0.15, 0.06], [0.158, 0.12], [0.15, 0.165], [0.115, 0.2], [0.065, 0.223], [0, 0.232],
  ] as const,
  hairDepth: 0.96,
  hairTilt: -0.1,
  /** Tousled crop (the default "cap" look): the base lathe above is just a
   *  scalp-hugging backing so no skin shows THROUGH the tufts — the actual
   *  hairstyle is this set of overlapping smooth-`ball` clumps. Each is
   *  [x, y, z, radius, shade] LOCAL to headY (×s at use). Varied sizes + a few
   *  taller crown clumps give the messy volume; the lower front row is an
   *  UNEVEN fringe (mismatched heights/sizes L↔R on purpose). `shade` nudges
   *  each clump's tone (±) so overlaps read as separate locks, not one blob. */
  hairTufts: [
    [0.0, 0.2, -0.02, 0.052, 0.0], // crown, center (low — texture, not height)
    [-0.07, 0.19, 0.03, 0.048, 0.06], // crown, left-front
    [0.065, 0.195, 0.0, 0.05, -0.06], // crown, right
    [-0.02, 0.19, -0.09, 0.052, 0.08], // crown, back
    [0.1, 0.178, -0.02, 0.044, 0.03], // side, right (HIGH — short sides)
    [-0.1, 0.174, -0.02, 0.044, -0.04], // side, left (HIGH — short sides)
    [-0.05, 0.172, 0.105, 0.046, 0.0], // fringe, left (high hairline)
    [0.05, 0.178, 0.108, 0.042, 0.06], // fringe, right (higher/smaller — uneven)
    [0.0, 0.16, 0.125, 0.04, 0.04], // fringe, center peak
    [0.0, 0.12, -0.12, 0.052, 0.05], // nape, center (hook the back down)
    [0.05, 0.125, -0.11, 0.05, 0.03], // nape, right
    [-0.05, 0.13, -0.11, 0.05, -0.03], // nape, left
  ] as const,
  // ---- Head frame + coverage shells + hair/beard recipes ----
  // Shared head map so hair cuts AND beard styles sit on the SAME skull
  // instead of freehand numbers. Points are LOCAL to headY (×s at use); they
  // are MATH only — studio Scalp overlay draws them (not the game).
  //   hairline*  mid/outer front hairline (just above brows)
  //   temple*    side hairline above the ear (undercut bare-zone edge)
  //   ear*       mid pinna (sideburn stop + beard side path; below/behind temple)
  //   crown      skull top / hair mass peak
  //   occipital  back of skull (high nape / back crown)
  //   nape       low nape (short-cut end)
  //   cheek*     mid cheek apple (sideburn meet / mustache wing)
  //   jaw*       jaw angle / jowl (ear → jaw → chin path)
  //   chin*      chin tip + sides (goatee width)
  //   lip        upper lip (mustache seat; just above mouthY)
  //   underlip   soul-patch seat under the lower lip
  scalp: {
    hairline: [0, 0.09, 0.12] as const,
    hairlineL: [-0.08, 0.1, 0.1] as const,
    hairlineR: [0.08, 0.1, 0.1] as const,
    templeL: [-0.14, 0.06, 0.02] as const,
    templeR: [0.14, 0.06, 0.02] as const,
    // Mid-ear: lower + slightly aft of temple so sideburns run temple → ear
    // and beards hang from ear toward the jaw without colliding with temple.
    earL: [-0.15, -0.01, -0.02] as const,
    earR: [0.15, -0.01, -0.02] as const,
    crown: [0, 0.19, -0.02] as const,
    occipital: [0, 0.12, -0.13] as const,
    // Low nape — short-cut end / sideburn-adjacent rear; lower than temple so
    // crew/swept falls don't stop mid-skull.
    nape: [0, -0.04, -0.12] as const,
    // ---- Jaw / beard map (front of egg; z proud of the face) ----
    cheekL: [-0.105, -0.01, 0.10] as const,
    cheekR: [0.105, -0.01, 0.10] as const,
    jawL: [-0.12, -0.10, 0.055] as const,
    jawR: [0.12, -0.10, 0.055] as const,
    chin: [0, -0.155, 0.118] as const,
    chinL: [-0.048, -0.14, 0.11] as const,
    chinR: [0.048, -0.14, 0.11] as const,
    // lip sits just above mouthY (−0.052); underlip just below for soul patch.
    lip: [0, -0.032, 0.142] as const,
    underlip: [0, -0.078, 0.132] as const,
  },
  /** Coverage shells: which band of the scalp the lathe mass covers.
   *  `full` = deep traveler shell (swept) — brim near ear/nape so no gap under
   *  the crown mass; `high` = raised front / flat crown (crew); `crown` = pad
   *  above temples only (undercut). Profile ys LOCAL to headY. Cap still uses
   *  `hairProfile` above — untouched. */
  hairShells: {
    full: {
      // Brim sits near ear height (y≈−0.02) so the shell itself reaches the
      // sideburns/nape band — locks only dress direction on top of that mass.
      profile: [
        [0.148, -0.03], [0.158, 0.03], [0.155, 0.09], [0.135, 0.145],
        [0.1, 0.19], [0.055, 0.225], [0, 0.242],
      ] as const,
      depth: 0.96,
      tilt: -0.2,
      zOff: -0.01,
    },
    high: {
      profile: [
        [0.148, 0.08], [0.152, 0.115], [0.148, 0.15], [0.118, 0.178], [0.065, 0.195], [0, 0.202],
      ] as const,
      depth: 0.94,
      tilt: -0.04,
      zOff: -0.012,
    },
    crown: {
      profile: [
        [0.05, 0.12], [0.095, 0.14], [0.118, 0.17], [0.105, 0.21], [0.06, 0.245], [0, 0.26],
      ] as const,
      depth: 0.88,
      tilt: -0.05,
      zOff: -0.01,
    },
  },
  /** Short-cut recipes (crew / undercut / swept). Each lock is a tapered bone
   *  from scalp landmark → landmark (+ optional off offsets). Volume balls
   *  sit at a landmark (+ off). Cap is NOT here — it keeps freehand tufts. */
  hairRecipes: {
    crew: {
      shell: "high" as const,
      locks: [
        // comb-back grain along the midline / near-mid
        { from: "hairline", to: "occipital", r0: 0.036, r1: 0.032, shade: 0, depth: 0.72, fromOff: [0, 0.06, -0.04], toOff: [0, 0.06, 0.04] },
        { from: "hairlineL", to: "occipital", r0: 0.03, r1: 0.027, shade: 0.05, depth: 0.72, fromOff: [0.02, 0.05, -0.03], toOff: [-0.03, 0.05, 0.04] },
        { from: "hairlineR", to: "occipital", r0: 0.03, r1: 0.027, shade: -0.05, depth: 0.72, fromOff: [-0.02, 0.05, -0.03], toOff: [0.03, 0.05, 0.04] },
        // high sides (temple → crown, short)
        { from: "templeL", to: "crown", r0: 0.028, r1: 0.022, shade: 0.03, depth: 0.85, fromOff: [0.01, 0.05, 0], toOff: [-0.04, -0.02, -0.02] },
        { from: "templeR", to: "crown", r0: 0.028, r1: 0.022, shade: -0.03, depth: 0.85, fromOff: [-0.01, 0.05, 0], toOff: [0.04, -0.02, -0.02] },
        // thin high fringe (almost none)
        { from: "hairlineL", to: "hairline", r0: 0.02, r1: 0.016, shade: 0.02, depth: 0.8, fromOff: [0.02, 0.04, 0], toOff: [-0.02, 0.05, -0.02] },
        { from: "hairlineR", to: "hairline", r0: 0.02, r1: 0.016, shade: -0.02, depth: 0.8, fromOff: [-0.02, 0.04, 0], toOff: [0.02, 0.05, -0.02] },
        // short nape
        { from: "occipital", to: "nape", r0: 0.04, r1: 0.028, shade: 0.04, depth: 0.55, fromOff: [0, 0, 0.02], toOff: [0, 0.03, 0] },
      ],
      volume: [
        { at: "crown", r: 0.034, shade: 0 },
        { at: "crown", r: 0.03, shade: 0.04, off: [-0.05, -0.01, -0.02] },
        { at: "crown", r: 0.03, shade: -0.04, off: [0.05, -0.01, -0.02] },
      ],
    },
    undercut: {
      shell: "crown" as const,
      locks: [
        // top mass flows FORWARD: occipital/crown → hairline
        { from: "occipital", to: "hairline", r0: 0.05, r1: 0.032, shade: 0, depth: 0.7, fromOff: [0, 0.08, 0.05], toOff: [0, 0.05, 0.01] },
        { from: "crown", to: "hairlineL", r0: 0.042, r1: 0.026, shade: 0.05, depth: 0.72, fromOff: [-0.03, 0.01, -0.02], toOff: [0.01, 0.04, 0.01] },
        { from: "crown", to: "hairlineR", r0: 0.04, r1: 0.025, shade: -0.05, depth: 0.72, fromOff: [0.03, 0.01, -0.02], toOff: [-0.01, 0.04, 0.01] },
        // outer edge of the top pad only (never down to temple — bare sides)
        { from: "crown", to: "hairlineL", r0: 0.032, r1: 0.022, shade: 0.04, depth: 0.78, fromOff: [-0.06, 0, 0], toOff: [0.02, 0.05, -0.03] },
        { from: "crown", to: "hairlineR", r0: 0.032, r1: 0.022, shade: -0.04, depth: 0.78, fromOff: [0.06, 0, 0], toOff: [-0.02, 0.05, -0.03] },
        // short rear of the top pad
        { from: "crown", to: "occipital", r0: 0.04, r1: 0.03, shade: 0.06, depth: 0.68, fromOff: [0, 0.02, -0.03], toOff: [0, 0.05, 0] },
      ],
      volume: [
        { at: "crown", r: 0.042, shade: 0, off: [0, 0.03, 0.02] },
        { at: "crown", r: 0.034, shade: 0.05, off: [-0.04, 0.01, 0.04] },
        { at: "crown", r: 0.034, shade: -0.05, off: [0.04, 0.01, 0.04] },
      ],
    },
    swept: {
      shell: "full" as const,
      locks: [
        // brush-back midline — ends AT nape (no lift offset; closes the rear gap)
        { from: "hairline", to: "nape", r0: 0.045, r1: 0.055, shade: 0, depth: 0.58, fromOff: [0, 0.03, -0.02], toOff: [0, 0.02, -0.01] },
        { from: "hairlineL", to: "nape", r0: 0.038, r1: 0.045, shade: 0.05, depth: 0.58, fromOff: [0.02, 0.02, -0.02], toOff: [-0.04, 0.02, 0] },
        { from: "hairlineR", to: "nape", r0: 0.038, r1: 0.045, shade: -0.05, depth: 0.58, fromOff: [-0.02, 0.02, -0.02], toOff: [0.04, 0.02, 0] },
        // crown → ear: fills the mid-side gap between top mass and pinna
        { from: "crown", to: "earL", r0: 0.042, r1: 0.036, shade: 0.04, depth: 0.7, fromOff: [-0.04, -0.01, 0], toOff: [0.01, 0.02, 0.01] },
        { from: "crown", to: "earR", r0: 0.042, r1: 0.036, shade: -0.04, depth: 0.7, fromOff: [0.04, -0.01, 0], toOff: [-0.01, 0.02, 0.01] },
        // temple → ear (thicker side curtain)
        { from: "templeL", to: "earL", r0: 0.04, r1: 0.038, shade: 0.03, depth: 0.72, fromOff: [0.01, 0.01, 0.01], toOff: [0.01, 0.015, 0.01] },
        { from: "templeR", to: "earR", r0: 0.04, r1: 0.038, shade: -0.03, depth: 0.72, fromOff: [-0.01, 0.01, 0.01], toOff: [-0.01, 0.015, 0.01] },
        // hairline corners → ear (front panel down to pinna)
        { from: "hairlineL", to: "earL", r0: 0.036, r1: 0.034, shade: 0.04, depth: 0.65, fromOff: [0.01, 0.02, -0.01], toOff: [0.01, 0.01, 0.01] },
        { from: "hairlineR", to: "earR", r0: 0.036, r1: 0.034, shade: -0.04, depth: 0.65, fromOff: [-0.01, 0.02, -0.01], toOff: [-0.01, 0.01, 0.01] },
        // occipital → ear (rear-side fill so crown doesn't float above the ears)
        { from: "occipital", to: "earL", r0: 0.04, r1: 0.034, shade: 0.03, depth: 0.6, fromOff: [-0.03, 0, 0.02], toOff: [0.01, 0.02, -0.01] },
        { from: "occipital", to: "earR", r0: 0.04, r1: 0.034, shade: -0.03, depth: 0.6, fromOff: [0.03, 0, 0.02], toOff: [-0.01, 0.02, -0.01] },
        // ear → nape + center nape fall (continuous lower band)
        { from: "earL", to: "nape", r0: 0.038, r1: 0.042, shade: 0.03, depth: 0.52, fromOff: [0.01, 0.01, -0.01], toOff: [-0.03, 0.015, 0] },
        { from: "earR", to: "nape", r0: 0.038, r1: 0.042, shade: -0.03, depth: 0.52, fromOff: [-0.01, 0.01, -0.01], toOff: [0.03, 0.015, 0] },
        { from: "occipital", to: "nape", r0: 0.06, r1: 0.045, shade: 0.05, depth: 0.48, fromOff: [0, 0, 0.02], toOff: [0, 0.01, -0.01] },
      ],
      volume: [
        { at: "crown", r: 0.04, shade: 0, off: [0, 0.01, -0.03] },
        { at: "occipital", r: 0.04, shade: 0.05, off: [-0.04, 0.02, 0.02] },
        { at: "occipital", r: 0.04, shade: -0.05, off: [0.04, 0.02, 0.02] },
        // mid-side plugs the shell↔ear gap
        { at: "templeL", r: 0.036, shade: 0.03, off: [0.01, -0.03, 0] },
        { at: "templeR", r: 0.036, shade: -0.03, off: [-0.01, -0.03, 0] },
        { at: "earL", r: 0.038, shade: 0.04, off: [0.015, 0.015, 0.01] },
        { at: "earR", r: 0.038, shade: -0.04, off: [-0.015, 0.015, 0.01] },
        { at: "nape", r: 0.042, shade: 0.04, off: [0, 0.02, 0.01] },
      ],
    },
  },
  /** Beard recipes (short / full / goatee / mustache). Same lock + volume
   *  language as hairRecipes — no coverage shell (beards are face mass, not a
   *  lathe dome). `none` is a no-op. Color = hair unless head.beard set. */
  beardRecipes: {
    short: {
      locks: [
        // ear → jaw (sideburns meet the jowl)
        { from: "earL" as ScalpPt, to: "jawL" as ScalpPt, r0: 0.028, r1: 0.032, shade: 0.03, depth: 0.7, fromOff: [0.01, -0.01, 0.02] as const, toOff: [0.01, 0.01, 0.02] as const },
        { from: "earR" as ScalpPt, to: "jawR" as ScalpPt, r0: 0.028, r1: 0.032, shade: -0.03, depth: 0.7, fromOff: [-0.01, -0.01, 0.02] as const, toOff: [-0.01, 0.01, 0.02] as const },
        // jaw → chin
        { from: "jawL" as ScalpPt, to: "chin" as ScalpPt, r0: 0.034, r1: 0.038, shade: 0.02, depth: 0.65, fromOff: [0.01, 0, 0.02] as const, toOff: [-0.01, -0.01, 0.01] as const },
        { from: "jawR" as ScalpPt, to: "chin" as ScalpPt, r0: 0.034, r1: 0.038, shade: -0.02, depth: 0.65, fromOff: [-0.01, 0, 0.02] as const, toOff: [0.01, -0.01, 0.01] as const },
        // cheek → chin (front face fill)
        { from: "cheekL" as ScalpPt, to: "chinL" as ScalpPt, r0: 0.026, r1: 0.03, shade: 0.04, depth: 0.72, fromOff: [0, -0.01, 0.01] as const },
        { from: "cheekR" as ScalpPt, to: "chinR" as ScalpPt, r0: 0.026, r1: 0.03, shade: -0.04, depth: 0.72, fromOff: [0, -0.01, 0.01] as const },
        // light mustache strip
        { from: "lip" as ScalpPt, to: "cheekL" as ScalpPt, r0: 0.014, r1: 0.016, shade: 0.02, depth: 0.8, fromOff: [-0.02, 0, 0] as const, toOff: [0.02, -0.02, 0] as const },
        { from: "lip" as ScalpPt, to: "cheekR" as ScalpPt, r0: 0.014, r1: 0.016, shade: -0.02, depth: 0.8, fromOff: [0.02, 0, 0] as const, toOff: [-0.02, -0.02, 0] as const },
      ],
      volume: [
        { at: "chin" as ScalpPt, r: 0.042, shade: 0, off: [0, -0.01, 0.01] as const },
        { at: "chinL" as ScalpPt, r: 0.03, shade: 0.03 },
        { at: "chinR" as ScalpPt, r: 0.03, shade: -0.03 },
        { at: "jawL" as ScalpPt, r: 0.032, shade: 0.02, off: [0.01, 0, 0.015] as const },
        { at: "jawR" as ScalpPt, r: 0.032, shade: -0.02, off: [-0.01, 0, 0.015] as const },
        { at: "underlip" as ScalpPt, r: 0.02, shade: 0.04 },
      ],
    },
    full: {
      locks: [
        // continuous side curtain: ear → jaw → chin (thicker than short)
        { from: "earL" as ScalpPt, to: "jawL" as ScalpPt, r0: 0.036, r1: 0.042, shade: 0.03, depth: 0.62, fromOff: [0.015, -0.01, 0.02] as const, toOff: [0.015, 0, 0.025] as const },
        { from: "earR" as ScalpPt, to: "jawR" as ScalpPt, r0: 0.036, r1: 0.042, shade: -0.03, depth: 0.62, fromOff: [-0.015, -0.01, 0.02] as const, toOff: [-0.015, 0, 0.025] as const },
        { from: "jawL" as ScalpPt, to: "chin" as ScalpPt, r0: 0.045, r1: 0.052, shade: 0.02, depth: 0.55, fromOff: [0.01, -0.01, 0.02] as const, toOff: [0, -0.02, 0.015] as const },
        { from: "jawR" as ScalpPt, to: "chin" as ScalpPt, r0: 0.045, r1: 0.052, shade: -0.02, depth: 0.55, fromOff: [-0.01, -0.01, 0.02] as const, toOff: [0, -0.02, 0.015] as const },
        // cheek cascade
        { from: "cheekL" as ScalpPt, to: "chinL" as ScalpPt, r0: 0.034, r1: 0.04, shade: 0.04, depth: 0.6, fromOff: [0, 0, 0.015] as const, toOff: [0, -0.015, 0.01] as const },
        { from: "cheekR" as ScalpPt, to: "chinR" as ScalpPt, r0: 0.034, r1: 0.04, shade: -0.04, depth: 0.6, fromOff: [0, 0, 0.015] as const, toOff: [0, -0.015, 0.01] as const },
        // lower hang (chin drops further)
        { from: "chinL" as ScalpPt, to: "chin" as ScalpPt, r0: 0.038, r1: 0.048, shade: 0.03, depth: 0.5, fromOff: [0, -0.01, 0] as const, toOff: [0, -0.04, 0.01] as const },
        { from: "chinR" as ScalpPt, to: "chin" as ScalpPt, r0: 0.038, r1: 0.048, shade: -0.03, depth: 0.5, fromOff: [0, -0.01, 0] as const, toOff: [0, -0.04, 0.01] as const },
        // mustache connected into cheeks
        { from: "lip" as ScalpPt, to: "cheekL" as ScalpPt, r0: 0.02, r1: 0.028, shade: 0.02, depth: 0.7, fromOff: [-0.015, 0, 0] as const, toOff: [0.02, -0.02, 0.01] as const },
        { from: "lip" as ScalpPt, to: "cheekR" as ScalpPt, r0: 0.02, r1: 0.028, shade: -0.02, depth: 0.7, fromOff: [0.015, 0, 0] as const, toOff: [-0.02, -0.02, 0.01] as const },
      ],
      volume: [
        { at: "chin" as ScalpPt, r: 0.058, shade: 0, off: [0, -0.03, 0.015] as const },
        { at: "chin" as ScalpPt, r: 0.042, shade: 0.05, off: [0, -0.06, 0.01] as const },
        { at: "chinL" as ScalpPt, r: 0.04, shade: 0.03, off: [0, -0.02, 0.01] as const },
        { at: "chinR" as ScalpPt, r: 0.04, shade: -0.03, off: [0, -0.02, 0.01] as const },
        { at: "jawL" as ScalpPt, r: 0.04, shade: 0.02, off: [0.015, 0, 0.02] as const },
        { at: "jawR" as ScalpPt, r: 0.04, shade: -0.02, off: [-0.015, 0, 0.02] as const },
        { at: "cheekL" as ScalpPt, r: 0.034, shade: 0.04, off: [0.01, -0.02, 0.01] as const },
        { at: "cheekR" as ScalpPt, r: 0.034, shade: -0.04, off: [-0.01, -0.02, 0.01] as const },
        { at: "underlip" as ScalpPt, r: 0.026, shade: 0.03 },
        { at: "lip" as ScalpPt, r: 0.024, shade: 0.02, off: [0, 0.005, -0.005] as const },
      ],
    },
    goatee: {
      locks: [
        // mustache → underlip → chin column only
        { from: "lip" as ScalpPt, to: "underlip" as ScalpPt, r0: 0.016, r1: 0.018, shade: 0.02, depth: 0.75, fromOff: [0, 0, -0.005] as const },
        { from: "underlip" as ScalpPt, to: "chin" as ScalpPt, r0: 0.022, r1: 0.034, shade: 0, depth: 0.6, toOff: [0, -0.02, 0.01] as const },
        { from: "chinL" as ScalpPt, to: "chin" as ScalpPt, r0: 0.024, r1: 0.032, shade: 0.03, depth: 0.65, fromOff: [0.01, 0, 0] as const, toOff: [0, -0.015, 0.01] as const },
        { from: "chinR" as ScalpPt, to: "chin" as ScalpPt, r0: 0.024, r1: 0.032, shade: -0.03, depth: 0.65, fromOff: [-0.01, 0, 0] as const, toOff: [0, -0.015, 0.01] as const },
        // thin mustache wings (not full cheeks)
        { from: "lip" as ScalpPt, to: "cheekL" as ScalpPt, r0: 0.012, r1: 0.014, shade: 0.04, depth: 0.85, fromOff: [-0.02, 0, 0] as const, toOff: [0.04, -0.03, -0.02] as const },
        { from: "lip" as ScalpPt, to: "cheekR" as ScalpPt, r0: 0.012, r1: 0.014, shade: -0.04, depth: 0.85, fromOff: [0.02, 0, 0] as const, toOff: [-0.04, -0.03, -0.02] as const },
      ],
      volume: [
        { at: "chin" as ScalpPt, r: 0.04, shade: 0, off: [0, -0.02, 0.015] as const },
        { at: "chin" as ScalpPt, r: 0.028, shade: 0.05, off: [0, -0.04, 0.01] as const },
        { at: "underlip" as ScalpPt, r: 0.022, shade: 0.03 },
        { at: "lip" as ScalpPt, r: 0.02, shade: 0.02, off: [0, 0.002, -0.005] as const },
      ],
    },
    mustache: {
      locks: [
        { from: "lip" as ScalpPt, to: "cheekL" as ScalpPt, r0: 0.016, r1: 0.014, shade: 0.03, depth: 0.7, fromOff: [-0.01, 0.005, 0] as const, toOff: [0.03, -0.02, -0.01] as const },
        { from: "lip" as ScalpPt, to: "cheekR" as ScalpPt, r0: 0.016, r1: 0.014, shade: -0.03, depth: 0.7, fromOff: [0.01, 0.005, 0] as const, toOff: [-0.03, -0.02, -0.01] as const },
      ],
      volume: [
        { at: "lip" as ScalpPt, r: 0.022, shade: 0, off: [0, 0.004, -0.005] as const },
        { at: "lip" as ScalpPt, r: 0.016, shade: 0.04, off: [-0.028, 0, -0.005] as const },
        { at: "lip" as ScalpPt, r: 0.016, shade: -0.04, off: [0.028, 0, -0.005] as const },
      ],
    },
  },
  /** Face — soft rounded eyes + a single smile curve, all in one place.
   *  Positions are LOCAL to headY; radii/sizes ×s at the use site. Eyes are
   *  smooth `ball`s: a white sclera flattened into the egg, a SMALL dark iris
   *  (a modest pupil in visible white — a gentle gaze, not a big black stare)
   *  and a catchlight. Brows are thin, near-level bars (a hair of inner-up so
   *  they read relaxed, never the inner-down "angry" tell). The mouth is ONE
   *  smooth curve: a chain of small overlapping balls along a shallow smile
   *  arc. Retune here, refresh, judge. */
  face: {
    eyeX: 0.058, eyeY: 0.022, // eye center (from head axis / headY)
    scleraR: 0.044, scleraZ: 0.108, scleraDepth: 0.42, // white, flattened into the face
    irisR: 0.02, irisZ: 0.124, irisDepth: 0.5, // dark, small — pupil in visible white
    glintR: 0.009, glintZ: 0.134, glintUp: 0.012, glintIn: 0.011, // catchlight, up-and-in
    yaw: 0.34, // features yaw to hug the cheek curve
    browY: 0.075, browZ: 0.112, browW: 0.05, browH: 0.011, browD: 0.026,
    browTilt: -0.04, // near-level, faint inner-up (relaxed, not stern) (× sx)
    // Smile = ONE smooth curve (the `arc` prim): a partial torus bowing up at
    // the corners. `mouthY` anchors the low center dip; radius/sweep set how
    // wide + how much it curves, tube = line thickness.
    mouthY: -0.052, mouthZ: 0.138, mouthRadius: 0.13, mouthTube: 0.0085, mouthSweep: 0.72,
  },
  /** Female frame preset (o.sex === "female"). Everything else — skeleton,
   *  pivots, head/face, hair, boots — is shared; only these override. `armScale`/
   *  `legScale` slim the limb capsules; `shoulderIn` pulls the arms inboard to
   *  meet the narrower shoulder shelf; `torsoProfile` is a full replacement
   *  silhouette (same y's as the male profile so height/anchors line up):
   *  rounder/fuller hips → a tight waist pinch → a slim ribcage → narrower
   *  shoulders. The BUST is NOT in the profile — a lathe revolves 360°, so a
   *  profile swell would bulge the BACK too — it's the front-only `bust` bulge
   *  below. */
  female: {
    armScale: 0.86, // arms slightly slimmer
    legScale: 0.9, // legs slightly slimmer
    shoulderIn: 0.022, // pull each arm in (×s) so the slimmer deltoid still plugs the shelf
    torsoProfile: [
      [0, 0.94], [0.15, 0.965], [0.203, 1.03], [0.214, 1.08], // rounder, fuller hips
      [0.155, 1.22], [0.148, 1.3], // tight waist (the hourglass pinch)
      [0.176, 1.42], [0.192, 1.5], // slim ribcage rising toward the shoulders
      [0.202, 1.575], [0.19, 1.632], [0.12, 1.671], [0, 1.69], // narrow shoulders → round-off
    ] as const,
    // Bust: the TORSO ITSELF bulges out in front — NOT a separate prim. These
    // are `lathe` frontBulge [y, dz] pairs (profile y-space, ×s at the call):
    // the torso mesh's front (+z) surface is pushed forward by dz at each height,
    // peaking over the chest and easing to 0 above/below, so it reads as the
    // rounded chest swell we liked — but only on the front; the back stays flat.
    // Raise dz for a fuller bust; shift the y's up/down to move it.
    bust: [
      [1.34, 0], [1.42, 0.058], [1.47, 0.078], [1.52, 0.055], [1.58, 0],
    ] as const,
  },
};

/** Named silhouette presets (the shared BODY_BUILDS list) — multiplier sets
 *  over HUMANOID_TUNING, so every doc shares one sculpted frame and picks a
 *  read (`body.build`). `limbR` scales the limb capsule radii, `torsoW` the
 *  torso/belt/skirt widths AND the shoulder spread (arms track the shelf),
 *  `depth` the torso front–back, `legLen` the hip/knee heights (shorter legs
 *  slide the whole upper body down — head size stays, so squat reads
 *  big-headed for free). Composes with the female frame. */
/** The rig's nominal hip line: the pelvis pivots sit at
 *  PELVIS_HIP_Y · scale · legLen above the feet, rigidly (bending the hips only
 *  swings the legs). pose-seat.ts derives every seat drop from this — ONE copy. */
export const PELVIS_HIP_Y = 1.06;

export const BUILD_PRESETS: Record<
  BodyBuild,
  { limbR: number; torsoW: number; depth: number; legLen: number }
> = {
  average: { limbR: 1, torsoW: 1, depth: 1, legLen: 1 },
  stout: { limbR: 1.16, torsoW: 1.14, depth: 1.14, legLen: 0.96 },
  slight: { limbR: 0.87, torsoW: 0.93, depth: 0.95, legLen: 1.02 },
  squat: { limbR: 1.14, torsoW: 1.1, depth: 1.08, legLen: 0.78 },
};

/** Frame scale factors + torso profile for a sex/build combination — the ONE
 *  derivation shared by the builder below and wear-scale.ts's formula
 *  baseline (so the two can never drift apart silently). */
export function frameScales(sex: Sex | undefined, build: BodyBuild | undefined) {
  const P = BUILD_PRESETS[build ?? "average"];
  const female = sex === "female";
  const FEM = HUMANOID_TUNING.female;
  return {
    P,
    female,
    armScale: (female ? FEM.armScale : 1) * P.limbR,
    legScale: (female ? FEM.legScale : 1) * P.limbR,
    torsoProfile: female ? FEM.torsoProfile : HUMANOID_TUNING.torsoProfile,
  };
}

/** Guard: every humanoid archetype anchor must be present. */
export function assertAnchors(points: Record<string, Vec3>): Record<string, Vec3> {
  for (const name of ARCHETYPE_ANCHORS.humanoid) {
    if (!(name in points)) throw new Error(`humanoid anchor "${name}" missing`);
  }
  return points;
}

/**
 * Scalp/jaw landmark (headY-local in HUMANOID_TUNING.scalp) → feet-space for the
 * builder. Optional per-lock offset is added in landmark units before ×s.
 */
export function scalpToWorld(
  pt: readonly [number, number, number],
  headY: number,
  s: number,
  off?: readonly [number, number, number],
): readonly [number, number, number] {
  const ox = off?.[0] ?? 0, oy = off?.[1] ?? 0, oz = off?.[2] ?? 0;
  return [(pt[0] + ox) * s, headY + (pt[1] + oy) * s, (pt[2] + oz) * s];
}

/**
 * Same landmark → head-joint local space (studio overlay parented on the head
 * limb). Lockstep with the builder:
 *   head pivot = (0, shoulderY − 0.02·s, 0)
 *   headY      = shoulderY + 0.23·s
 *   → local y  = (0.25 + scalpY)·s
 */
export function scalpToHeadLocal(
  pt: readonly [number, number, number],
  s: number,
  off?: readonly [number, number, number],
): readonly [number, number, number] {
  const ox = off?.[0] ?? 0, oy = off?.[1] ?? 0, oz = off?.[2] ?? 0;
  return [(pt[0] + ox) * s, (0.25 + pt[1] + oy) * s, (pt[2] + oz) * s];
}

/** Build a humanoid (feet at y=0, facing +z) and return its anchor points.
 *  Tapered-capsule limbs (a shared ball of flesh at each joint, so segments
 *  stay seamless while the gait folds them), lathe torso/head/hair, smooth
 *  ball-and-arc face. Fully jointed: hips→knees and shoulders→elbows are
 *  NESTED limb pivots, and the upper body hangs off a `torso` pivot with a
 *  `head` pivot inside it — gait.ts folds/counter-twists them for the stride. */
export function humanoid(p: Prims, o: HumanoidOptions): Record<string, Vec3> {
  const { box, cylinder, ball, bone, lathe, limb, socket } = p;
  const T = HUMANOID_TUNING;
  // Base scale-up for the whole frame — it reads slightly small next to the
  // world otherwise. Multiplies with a doc's own body.scale.
  const s = (o.scale ?? 1) * 1.1;

  // Female frame: slimmer limbs, a rounder-hipped/tapered torso + a small bust.
  // Everything not touched here is shared with the male build. frameScales is
  // the shared derivation (wear-scale.ts reads the same numbers).
  const { P, female, armScale, legScale, torsoProfile } = frameScales(o.sex, o.build);
  const FEM = T.female;

  const hipY = PELVIS_HIP_Y * s * P.legLen;
  const kneeY = 0.58 * s * P.legLen;
  const waistH = 0.24 * s;
  const chestH = 0.34 * s;
  const chestW = 0.46 * s * P.torsoW;
  const shoulderY = hipY + waistH + chestH;
  const armLen = (o.arms?.length ?? 0.68) * s;
  const armX = chestW / 2 + T.armInset * s - (female ? FEM.shoulderIn * s : 0);
  const elbowY = shoulderY - 0.37 * s;
  const wristY = shoulderY - armLen - 0.02 * s;
  const handY = shoulderY - armLen - 0.06 * s;
  const headY = shoulderY + 0.23 * s;
  // Shorter legs (preset legLen) slide the whole upper body down; the tuning's
  // ABSOLUTE-y profiles (authored against the nominal 1.06 hip line) shift
  // down with it. dy = 0 on the average build.
  const dy = hipY - PELVIS_HIP_Y * s;
  const torsoDepth = T.torsoDepth * P.depth;
  type Profile = readonly (readonly [number, number])[];
  /** Absolute-y profile (lathe baseY 0): ×s, preset width ×w + the legLen drop. */
  const profAbs = (pr: Profile, w = 1) =>
    pr.map(([r, y]) => [r * s * w, y * s + dy] as const);
  /** Local-y profile (y relative to the lathe's own baseY): plain ×s. */
  const profLocal = (pr: Profile) => pr.map(([r, y]) => [r * s, y * s] as const);

  // ---- Lower body: hip pivot → thigh bone → knee pivot → shin bone + shoe ----
  if (o.lower.kind === "legs") {
    const c = o.lower.color;
    const boot = o.lower.boot ?? shade(c, -0.4);
    const shin = o.lower.short ? shade(o.skin, -0.06) : shade(c, -0.14);
    for (const sx of [-1, 1]) {
      const lx = sx * 0.11 * s;
      limb(sx < 0 ? "hipL" : "hipR", [lx, hipY, 0], () => {
        bone(lx, hipY, 0, lx, kneeY, 0, T.thigh[0] * s * legScale, T.thigh[1] * s * legScale, c, undefined, T.belly.thigh); // thigh
        limb(sx < 0 ? "kneeL" : "kneeR", [lx, kneeY, 0], () => {
          bone(lx, kneeY, 0, lx, T.ankleY * s, 0, T.shin[0] * s * legScale, T.shin[1] * s * legScale, shin, undefined, T.belly.shin); // shin
          const B = T.boot;
          // One flattened boot capsule (flat-ish bottom, no proud sole shelf) +
          // the ankle bridge buried into it.
          bone(lx, B.y * s, B.heelZ * s, lx, B.y * s, B.toeZ * s, B.heelR * s, B.toeR * s, boot, B.depth); // boot
          bone(lx, B.ankleTopY * s, B.ankleZ * s, lx, B.ankleBotY * s, B.ankleZ * s, B.ankleR * s, B.ankleBotR * s, boot); // ankle bridge
        });
      });
    }
    // (no pelvis mass — the torso lathe rounds under the hips and covers it)
  } else {
    // Rounded bell skirt: ONE lathe from the ground to the hip line, tucked
    // under the torso lathe. y's are nominal hip-space — scaled by legLen so
    // the waist tracks a shortened build; width follows torsoW. No leg joints
    // on a skirt build (the sparse rig contract — animators no-op on missing).
    const sk = (pr: Profile) =>
      pr.map(([r, y]) => [r * s * P.torsoW, y * s * P.legLen] as const);
    lathe(0, 0, 0, sk(T.skirtProfile), o.lower.color, T.skirtDepth * P.depth, 12);
    if (o.lower.hem !== undefined)
      lathe(0, 0, 0, sk(T.skirtHemProfile), o.lower.hem, T.skirtDepth * P.depth + 0.02, 12);
  }
  lathe(0, 0, 0, profAbs(T.beltProfile, P.torsoW), shade(o.torso.color, -0.45), torsoDepth + 0.02, 10); // belt lens

  const tc = o.torso.color;
  const sleeve = o.arms?.sleeve ?? tc;
  const hair = o.head?.hair;
  const style = o.head?.hairStyle ?? (hair !== undefined ? "cap" : "none");
  // Player Appearance forbids female beards; enforce the same on the female
  // frame so a stray head.beardStyle can't render facial hair on that body.
  const beardStyle = (female ? "none" : (o.head?.beardStyle ?? "none")) as BeardStyle;
  // Beard color: explicit head.beard → hair color → muted skin (bald + beard).
  const beardColor = o.head?.beard ?? hair ?? shade(o.skin, -0.45);
  // Bust frontBulge pairs are [y, dz] (not [r, y]) — shift y with the frame,
  // scale the push by s only.
  const bust = female
    ? FEM.bust.map(([y, dz]) => [y * s + dy, dz * s] as const)
    : undefined;

  limb("torso", [0, hipY, 0], () => {
    // Tunic torso: ONE custom lathe silhouette — hips round under (covers the
    // pelvis), waist pinches, chest bulges, shoulders round off below the neck.
    // The female preset swaps in a rounder-hipped, tight-waisted hourglass AND
    // grows the bust as a FRONT-ONLY bulge of this same torso mesh (frontBulge),
    // so the chest swells forward while the back stays flat — no separate prim.
    lathe(0, 0, 0, profAbs(torsoProfile, P.torsoW), tc, torsoDepth, 10, undefined, bust);

    // Arms: ONE continuous arm capsule per side — its fat top cap IS the
    // deltoid (no separate shoulder ball), rooted high + inboard so it plugs
    // into the widened torso shoulder shelf.
    for (const sx of [-1, 1]) {
      const ax = sx * armX;
      limb(sx < 0 ? "shoulderL" : "shoulderR", [ax, shoulderY - T.shoulderDrop * s, 0], () => {
        bone(ax, shoulderY - T.shoulderDrop * s, 0, ax, elbowY, 0, T.upperArm[0] * s * armScale, T.upperArm[1] * s * armScale, sleeve, undefined, T.belly.upperArm);
        limb(sx < 0 ? "elbowL" : "elbowR", [ax, elbowY, 0], () => {
          bone(ax, elbowY, 0, ax, wristY, 0, T.forearm[0] * s * armScale, T.forearm[1] * s * armScale, shade(sleeve, -0.12), undefined, T.belly.forearm);
          ball(ax, handY, 0.02 * s, 0.062 * s, o.skin, 0.82); // hand — smooth ball, slightly flattened front–back into a fist
          socket(sx < 0 ? "handMain" : "handOff", [ax, handY, 0.02 * s]);
        });
      });
    }

    // Head pivot → neck + head.
    limb("head", [0, shoulderY - 0.02 * s, 0], () => {
      cylinder(0, shoulderY - 0.02 * s, 0, 0.055 * s, 0.068 * s, 0.13 * s, o.skin);

      // Egg head: a custom lathe silhouette (chin narrower, crown domed).
      lathe(0, 0, 0, profAbs(T.headProfile), o.skin, T.headDepth, 10);
      // One landmark resolver for hair + beard (feet-space via scalpToWorld).
      const scalpAt = (pt: ScalpPt, off?: readonly [number, number, number]) =>
        scalpToWorld(T.scalp[pt], headY, s, off);

      if (hair !== undefined && style !== "none") {
        // Shared recipe path: coverage shell + landmark locks + sparse volume.
        // Used by crew/undercut/swept; cap keeps freehand tufts below.
        const emitRecipe = (id: keyof typeof T.hairRecipes) => {
          const recipe = T.hairRecipes[id];
          const shell = T.hairShells[recipe.shell];
          lathe(0, headY, shell.zOff * s, profLocal(shell.profile), hair, shell.depth, 10, [shell.tilt, 0, 0]);
          for (const lock of recipe.locks) {
            const a = scalpAt(lock.from as ScalpPt, lock.fromOff);
            const b = scalpAt(lock.to as ScalpPt, lock.toOff);
            bone(a[0], a[1], a[2], b[0], b[1], b[2], lock.r0 * s, lock.r1 * s, shade(hair, lock.shade), lock.depth);
          }
          for (const v of recipe.volume) {
            const p = scalpAt(v.at as ScalpPt, v.off);
            ball(p[0], p[1], p[2], v.r * s, shade(hair, v.shade), 0.88);
          }
        };

        if (style === "mohawk") {
          // A thin crest fin front→back — SOFT_BOX is a rounded box, so it
          // still reads soft against the capsule vocabulary. No dome.
          box(0, headY + 0.19 * s, -0.02 * s, 0.05 * s, 0.16 * s, 0.3 * s, hair, [-0.08, 0, 0]);
        } else if (style === "crew" || style === "undercut" || style === "swept") {
          emitRecipe(style);
        } else {
          // Base hair mass: a scalp-hugging cap shell (tilted back a touch) —
          // for "cap" it's backing under the tousle tufts so no skin shows
          // through; for the tied styles it's the visible crown.
          lathe(0, headY, -0.012 * s, profLocal(T.hairProfile), hair, T.hairDepth, 10, [T.hairTilt, 0, 0]);
          if (style === "cap") {
            // Tousled crop: overlapping smooth-ball clumps (varied size/height +
            // an uneven fringe) build an irregular, messy surface — no helmet.
            for (const [tx, ty, tz, tr, sh] of T.hairTufts)
              ball(tx * s, headY + ty * s, tz * s, tr * s, shade(hair, sh), 0.9);
          }
          if (style === "bun") ball(0, headY + 0.1 * s, -0.17 * s, 0.08 * s, hair);
          if (style === "long") {
            // Back curtain: a near-vertical flattened capsule hugging the nape,
            // falling to the shoulder line (depth squashes it front–back).
            bone(0, headY + 0.04 * s, -0.12 * s, 0, headY - 0.32 * s, -0.15 * s, 0.125 * s, 0.085 * s, hair, 0.5);
          }
          if (style === "ponytail") {
            ball(0, headY + 0.07 * s, -0.15 * s, 0.045 * s, shade(hair, -0.25)); // tie
            bone(0, headY + 0.07 * s, -0.15 * s, 0, headY - 0.26 * s, -0.22 * s, 0.05 * s, 0.028 * s, hair); // tail
          }
        }
      }
      // Face features ride the CURVED egg surface (each off-center piece yaws to
      // the local surface tangent so it hugs the cheek). The expression pose —
      // brows / eyes / mouth — lives in src/model/face.ts; "happy" (the default)
      // reproduces the historical smile byte-for-byte. Skip when the model brings
      // its own full face via slots.face (goblin) — built-in beards leave this on
      // (they only add jaw/chin mass over the smile).
      if (o.head?.features !== false) {
        buildHumanoidFace(p, {
          headY, s, F: T.face,
          eye: o.head?.eye ?? 0x1b1f25,
          browColor: hair ?? shade(o.skin, -0.35),
          skin: o.skin,
          expression: o.head?.expression ?? "happy",
        });
      }

      // Built-in beard styles (BEARD_STYLES) — landmark locks + volume on the
      // jaw/chin map. Face-add parts (`beard-gray`) still work as an alternate
      // custom path; prefer recipes for new characters.
      if (beardStyle !== "none") {
        const recipe = (T.beardRecipes as Record<string, (typeof T.beardRecipes)["short"]>)[beardStyle];
        if (recipe) {
          for (const lock of recipe.locks) {
            const a = scalpAt(lock.from as ScalpPt, lock.fromOff);
            const b = scalpAt(lock.to as ScalpPt, lock.toOff);
            bone(a[0], a[1], a[2], b[0], b[1], b[2], lock.r0 * s, lock.r1 * s, shade(beardColor, lock.shade), lock.depth);
          }
          for (const v of recipe.volume) {
            const pnt = scalpAt(v.at as ScalpPt, v.off);
            ball(pnt[0], pnt[1], pnt[2], v.r * s, shade(beardColor, v.shade), 0.9);
          }
        }
      }
    });
  });

  // Shoulder shelf = rest seat of the upper-arm root (same as limb pivot).
  const shelfY = shoulderY - T.shoulderDrop * s;
  return assertAnchors({
    root: [0, 0, 0],
    crown: [0, headY + 0.19 * s, 0],
    face: [0, headY, 0.15 * s],
    chest: [0, hipY + waistH + chestH / 2, 0.135 * s * P.depth],
    back: [0, hipY + waistH + chestH / 2, -0.135 * s * P.depth],
    beltFront: [0, hipY, 0.125 * s * P.depth],
    handL: [-armX, handY, 0.02 * s],
    handR: [armX, handY, 0.02 * s],
    // Wearable-slot anchors (torsowear/legwear/footwear — see ARCHETYPE_SLOTS):
    // torso = chest center, legL/R = thigh centers (inside the hip limbs),
    // footL/R = shoe centers (inside the knee limbs). Published on the skirt
    // build too, at the same nominal spots, so the anchor set stays total.
    torso: [0, hipY + waistH + chestH / 2, 0],
    legL: [-0.11 * s, (hipY + kneeY) / 2, 0],
    legR: [0.11 * s, (hipY + kneeY) / 2, 0],
    footL: [-0.11 * s, 0.07 * s, 0.05 * s],
    footR: [0.11 * s, 0.07 * s, 0.05 * s],
    // Wear helpers — content/parts fillers may set part.anchor to these
    // (with limb: shoulderL/R for pads that must follow the arm).
    shoulderShelfL: [-armX, shelfY, 0],
    shoulderShelfR: [armX, shelfY, 0],
    collar: [0, shoulderY - 0.02 * s, 0.04 * s],
    belt: [0, hipY, 0.02 * s],
  });
}
